import { useCallback } from "react";
import Space from "../../../passapp-sdk/space";
import { SpaceEventAction, SpaceScanData } from "../../../passapp-sdk/types/spaceTypes";
import { useStore } from "../../../hooks/useStore";

const space = new Space();

const SCAN_ERROR = "No pudimos validar el QR. Reintentá el escaneo.";
const EVENT_ERROR = "No pudimos registrar la decisión. Reintentá.";

const asError = (err: any, fallback: string) =>
  new Error(typeof err === "string" && err ? err : err?.message || fallback);

const useSpaceScan = () => {
  const { authStore } = useStore();

  // Escanea el QR del comensal, resuelve el caso y devuelve lo que salió de las
  // reglas del espacio.
  const scanQr = useCallback(
    (qr: string): Promise<SpaceScanData> =>
      space
        .scanSpace(authStore.token, qr)
        .then((response: any) => {
          const data = response?.data;
          if (!data?.status) throw new Error(SCAN_ERROR);
          return data as SpaceScanData;
        })
        .catch((err: any) => {
          throw asError(err, SCAN_ERROR);
        }),
    [authStore.token]
  );

  // Decisión del operador para el caso que el scan deja sin evento. El motivo no
  // se manda: el servidor lo resuelve de nuevo, porque entre el escaneo y el tap
  // pudo cambiar algo.
  const registerDecision = useCallback(
    (action: SpaceEventAction, scan: SpaceScanData): Promise<SpaceScanData> => {
      if (!scan.user) return Promise.reject(new Error(EVENT_ERROR));

      return space
        .addSpaceEvent(authStore.token, scan.user.id, action)
        .then((response: any) => {
          const data = response?.data;
          if (!data?.status) {
            // Respuesta sin `data.status`: al operador le llega el mensaje genérico, así que
            // dejamos la respuesta cruda en consola para poder ver qué mandó el backend.
            console.error("addSpaceEvent sin data.status", { action, response });
            throw new Error(EVENT_ERROR);
          }
          return data as SpaceScanData;
        })
        .catch((err: any) => {
          // `HttpRequest` rechaza con `json.msg`: si viene vacío (o el body no es JSON) se pierde
          // el motivo y sólo queda el mensaje genérico de `asError`.
          console.error("addSpaceEvent falló", { action, scanStatus: scan.status, err });
          throw asError(err, EVENT_ERROR);
        });
    },
    [authStore.token]
  );

  return { scanQr, registerDecision };
};

export default useSpaceScan;
